<template>
  <div id="loaderWrapper">
    <div class="d-flex justify-content-between align-items-center p-4">
      <div class="card-header border-0 pl-3 pt-2">
        <h2 class="mb-0">Jaarkalender</h2>
        <p class="text-gray mb-0">
          Hier vind je een jaarkalender en de mogelijkheid om op een visueel aanschouwelijke manier de verhuur over meerdere jaren heen te vergelijken. De jaarkalender maakt voor het verleden gebruik van de gegevens van de statistiekenmodule (enkel afgeronde boekingen), voor de toekomst wordt de online kalender geraadpleegd.
          Uitbaters die gebruik maken van de boekingsformules Plus of Service, kunnen toekomstige online boekingen via deze jaarkalender snel bekijken (doorklikken naar de dossierdetails). Je kan hier echter geen aanpassingen doen (onbeschikbaar maken, vaste boekingsperiodes toevoegen e.d.).
        </p>
      </div>

      <div class="form-select-wrapper">
        <select class="p-3 form-select form-select-lg form-select-solid w-20 js-accommodation" @change="getYearCalendarData($event.target.value)">
          <option value="all">Alle</option>
          <option v-if="accommodationEntities.length > 0" v-for="entity in accommodationEntities" :value="entity.uuid">{{ entity.label }}</option>
        </select>
      </div>
    </div>
    <Calendar
      :data-source="dataSource"
      :year="currentYear"
      :language="language"
      :context-menu-items="contextMenuItems"
      @click-day="clickDay"
      @year-changed="changeSelectedYear"
    ></Calendar>

    <div class="card-footer d-flex justify-content-between align-items-center pt-6">
      <div class="d-flex">
        <div class="mr-4">
          <input class="form-check-input mr-2" type="checkbox">
          <span>Vrij</span>
        </div>
        <div class="mr-4">
          <input class="form-check-input legend legend-red mr-2" type="checkbox">
          <span>Boeking</span>
        </div>
        <div class="mr-4">
          <input class="form-check-input legend legend-gray mr-2" type="checkbox">
          <span>Gesloten</span>
        </div>
      </div>
      <a v-if="selectedAccommodation && 'all' != selectedAccommodation" class="btn btn-orange btn-sm" target="_blank"
         :href="'/nl/manager-portal/accommodations/' + selectedAccommodation + '/edit/calendar/' + currentYear">
        Werk in je kalender
      </a>
    </div>

    <div v-if="showModal" @click.self="closeModal" class="year-calendar-modal">
      <div class="modal-content">
        <div class="modal-body">
          <div class="d-flex justify-content-between">
            <p class="text-dark fw-bolder">Kalenderevents</p>
            <i @click="closeModal" class="icon-close"></i>
          </div>

          <div v-for="modalEvent in modalEvents" class="event_row mt-3">
            <div class="info__row border-bottom-dashed">
              <span>Datum</span>
              <span class="fw-bolder">{{ modalEvent.startDate }} tot {{ modalEvent.endDate }}</span>
            </div>
            <div v-if="null != modalEvent.startTime" class="info__row border-bottom-dashed">
              <span>Aankomst</span>
              <span class="fw-bolder">{{ modalEvent.startTime }}</span>
            </div>
            <div v-if="null != modalEvent.endTime" :class="{
              'info__row': true,
              'border-bottom-dashed': null != modalEvent.groupName,
            }">
              <span>Vertrek</span>
              <span class="fw-bolder">{{ modalEvent.endTime }}</span>
            </div>
            <div v-if="null != modalEvent.groupName" class="info__row">
              <span>Groep</span>
              <span class="fw-bolder">{{ modalEvent.groupName }}</span>
            </div>

            <div v-if="modalEvent.bookingId && canViewBooking" class="d-flex justify-content-end mb-3">
              <a :href="'/nl/manager-portal/booking/' + modalEvent.bookingId" target="_blank" class="btn-view-booking mt-3">
                Details

                <span class="svg-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect opacity="0.5" x="18" y="13" width="13" height="2" rx="1" transform="rotate(-180 18 13)" fill="currentColor"></rect>
                    <path d="M15.4343 12.5657L11.25 16.75C10.8358 17.1642 10.8358 17.8358 11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25L18.2929 12.7071C18.6834 12.3166 18.6834 11.6834 18.2929 11.2929L12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75C10.8358 6.16421 10.8358 6.83579 11.25 7.25L15.4343 11.4343C15.7467 11.7467 15.7467 12.2533 15.4343 12.5657Z" fill="currentColor"></path>
                </svg>
            </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="isLoading" class="spinner-overlay">
    <div class="spinner"></div>
  </div>

</template>

<script setup>
import {ref, onMounted} from 'vue';
import Calendar from './YearCalendarWidget.vue';

const language = ref('nl');
const selectedAccommodation = ref(null);
const canViewBooking = ref(false);

const showModal = ref(false);
const isLoading = ref(false);
const modalEvents = ref([]);

const dataSource = ref([]);
const accommodationEntities = ref([]);

const currentYear = ref(new Date().getFullYear());

function changeSelectedYear(year) {
  if (currentYear.value !== year.currentYear){
    currentYear.value = year.currentYear;

    let selectedOption = document.querySelector('.js-accommodation');

    getYearCalendarData(selectedOption.value);
  }
}

async function getYearCalendarData(id) {
  let url = `/nl/manager-portal/domain/calendar/year/${currentYear.value}`;
  selectedAccommodation.value = id;

  if (undefined !== id && 'all' !== id) {
    url = `/nl/manager-portal/accommodations/${selectedAccommodation.value}/calendar/year/${currentYear.value}`;
  }

  isLoading.value = true;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new ResponseError('Bad fetch response', response);
    }

    const responseData = await response.json();

    responseData.forEach((data) => {
      switch (data.type) {
        case 'booking':
          data.color = '#fdb9b9'; // reddish pink
          break;
        case 'unavailable':
          data.color = '#888787'; // gray - black
          break;
        default: 
          // should not happen, only if data.type is not one of the above types
          data.color = '#edf7ff'; // CJT light blue
          break;
      }

      const formattedStartDate = new Date(data.startDate).toISOString();
      const formattedEndDate = new Date(data.endDate).toISOString();

      data.name = data.translatedType;
      data.location = data.source;
      data.startDate = new Date(formattedStartDate);
      data.startDate.setHours(0, 0, 0, 0);
      data.endDate = new Date(formattedEndDate);
      data.endDate.setHours(0, 0, 0, 0);
    });

    dataSource.value = responseData;
  } catch (error) {
    dataSource.value = [];
    isLoading.value = false;
  } finally {
    isLoading.value = false;
  }
}

const getYearCalendarAccommodationEntities = async () => {
  try {
    const response = await fetch('/nl/manager-portal/accommodations/calendar/year/list');

    if (!response.ok) {
        throw new ResponseError('Bad fetch response', response);
    }

    accommodationEntities.value = await response.json();
    await getYearCalendarData();

  } catch (error) {
    accommodationEntities.value = [];
  } finally {
    isLoading.value = false;
  }
}

const contextMenuItems = [
  {
    click: (evt) => {
      showModal.value = true;
    },
  },
];

const clickDay = (event) => {
  if (event.events.length > 0) {
    modalEvents.value = [];

    event.events.forEach((event) => {
      let modalEvent = {
        'startDate': moment(event.startDate).format('DD-MM-YYYY'),
        'endDate': moment(event.endDate).format('DD-MM-YYYY'),
        'bookingId': event.bookingId,
      };

      if ('unavailable' !== event.groupName && 'Niet beschikbaar' !== event.groupName) {
        modalEvent.groupName = event.groupName;
      }

      if ('00:00' !== event.startTime) {
        modalEvent.startTime = event.startTime;
      }

      if ('00:00' !== event.endTime) {
        modalEvent.endTime = event.endTime;
      }

      modalEvents.value.push(modalEvent);
    });

    showModal.value = true;
  }
};

const closeModal = () => {
  showModal.value = false;
};

onMounted(async () => {
  const el = document.getElementById('yearCalendarWidgetWrapper');

  if (undefined !== el?.dataset?.canViewBooking) {
    canViewBooking.value = el.dataset.canViewBooking === '1' || el.dataset.canViewBooking === 'true';
  }

  await getYearCalendarAccommodationEntities();
})
</script>
